/**
 * Done. / ChiroCPD — Typed Fetch Wrapper
 *
 * All API calls go through this module. Handles:
 *   - credentials: 'include' for httpOnly cookie auth
 *   - 401 → redirect to Stripe checkout / login
 *   - 403 → subscription expired state
 *   - Cap sidecar auto-extraction from MutationResponse
 *   - HTTP-only clipboard warning in development
 */

import type { CapStatus, MutationResponse } from '@/lib/types';

// ── Cap sidecar listener ─────────────────────────────────────────────────────
// CapStatusContext registers a listener here so any mutation that returns
// a capStatus payload automatically updates the global cap display.

type CapStatusListener = (cap: CapStatus) => void;
let capStatusListener: CapStatusListener | null = null;

export function setCapStatusListener(fn: CapStatusListener): void {
  capStatusListener = fn;
}

export function clearCapStatusListener(): void {
  capStatusListener = null;
}

// ── Base URL ─────────────────────────────────────────────────────────────────
// In development Vite proxies /api → http://localhost:8787 (see vite.config.ts).
// VITE_API_URL is only needed for standalone builds or production overrides.

function baseUrl(): string {
  return (import.meta.env.VITE_API_URL as string | undefined) ?? '';
}

// ── Core request function ────────────────────────────────────────────────────

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal | undefined;
}

class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string | undefined,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export { ApiError };

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const fetchInit: RequestInit = {
    method,
    headers,
    credentials: 'include',
  };
  if (body !== undefined) fetchInit.body = JSON.stringify(body);
  if (signal !== undefined) fetchInit.signal = signal;

  const response = await fetch(`${baseUrl()}${path}`, fetchInit);

  // ── Auth / subscription gate ─────────────────────────────────────────────
  if (response.status === 401) {
    // Not authenticated — throw ApiError, let components handle it
    // (RouteGuard shows LandingPage, other pages can redirect as needed)
    throw new ApiError(401, 'UNAUTHENTICATED', 'Session expired. Redirecting…');
  }

  if (response.status === 403) {
    // Subscription lapsed — the app handles this via CapStatusContext
    throw new ApiError(403, 'SUBSCRIPTION_INACTIVE', 'Subscription inactive or expired.');
  }

  if (!response.ok) {
    let code: string | undefined;
    let message = `HTTP ${String(response.status)}`;
    try {
      const json = await response.json() as { error?: string; code?: string };
      message = json.error ?? message;
      code = json.code;
    } catch {
      // ignore JSON parse error
    }
    throw new ApiError(response.status, code, message);
  }

  // ── Cap sidecar extraction ───────────────────────────────────────────────
  // Mutation endpoints return { data, capStatus }. We extract capStatus
  // and notify the listener, then return data transparently.
  const json: unknown = await response.json();

  if (
    capStatusListener &&
    json !== null &&
    typeof json === 'object' &&
    'capStatus' in json &&
    'data' in json
  ) {
    const mutation = json as MutationResponse;
    capStatusListener(mutation.capStatus);
    return mutation.data as T;
  }

  return json as T;
}

// ── Public API ────────────────────────────────────────────────────────────────

export const api = {
  get<T>(path: string, signal?: AbortSignal): Promise<T> {
    const opts: RequestOptions = { method: 'GET' };
    if (signal !== undefined) opts.signal = signal;
    return request<T>(path, opts);
  },

  post<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
    const opts: RequestOptions = { method: 'POST', body };
    if (signal !== undefined) opts.signal = signal;
    return request<T>(path, opts);
  },

  put<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
    const opts: RequestOptions = { method: 'PUT', body };
    if (signal !== undefined) opts.signal = signal;
    return request<T>(path, opts);
  },

  patch<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
    const opts: RequestOptions = { method: 'PATCH', body };
    if (signal !== undefined) opts.signal = signal;
    return request<T>(path, opts);
  },

  delete<T>(path: string, signal?: AbortSignal): Promise<T> {
    const opts: RequestOptions = { method: 'DELETE' };
    if (signal !== undefined) opts.signal = signal;
    return request<T>(path, opts);
  },
} as const;
